import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../context/AuthContext';
import { DataContext } from '../../../context/DataContext';
import {
  getActivitiesInRange,
  getUserShareCount,
  getUserElevationCount,
  getMonthlyGoals,
  getAllUserActivities
} from '../../../firebase/services';
import { BottomNav } from '../../common/BottomNav';
import { FeedbackLink } from '../../common/FeedbackLink';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { downloadAllActivitiesICS } from '../../../utils/calendarUtils';
import './advocacy-summary.css';

const AdvocacySummaryScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const { causes } = useContext(DataContext);

  const [loading, setLoading] = useState(true);
  const [allTimeStats, setAllTimeStats] = useState({
    total: 0,
    withReflections: 0,
    withPhotos: 0,
    shared: 0,
    elevations: 0,
    causesActive: 0
  });
  const [thisMonthData, setThisMonthData] = useState({
    total: 0,
    byCause: {},
    prevMonthTotal: 0,
    prevMonthName: '',
    isFirstMonth: false
  });
  const [thisYearData, setThisYearData] = useState({
    total: 0,
    byCause: {},
    busiestMonth: '',
    busiestMonthCount: 0
  });
  const [monthlyChartData, setMonthlyChartData] = useState([]);
  const [goalChartData, setGoalChartData] = useState([]);
  const [allActivities, setAllActivities] = useState([]);

  useEffect(() => {
    if (!currentUser) return;
    fetchAllData();
    // Load once per signed-in user; fetchAllData is redefined every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      // Date ranges
      const yearStart = new Date(currentYear, 0, 1);
      const monthStart = new Date(currentYear, currentMonth, 1);
      const monthEnd = new Date(currentYear, currentMonth + 1, 1);
      const prevMonthStart = new Date(currentYear, currentMonth - 1, 1);
      const prevMonthEnd = new Date(currentYear, currentMonth, 1);

      // 6 months ago for chart data
      const sixMonthsAgo = new Date(currentYear, currentMonth - 5, 1);

      // Fetch all data in parallel
      const [
        yearActivities,
        prevMonthActivities,
        allActivities,
        shareCount,
        elevationCount,
        goalData
      ] = await Promise.all([
        getActivitiesInRange(currentUser.uid, sixMonthsAgo, monthEnd),
        getActivitiesInRange(currentUser.uid, prevMonthStart, prevMonthEnd),
        getAllUserActivities(currentUser.uid),
        getUserShareCount(currentUser.uid),
        getUserElevationCount(currentUser.uid),
        getMonthlyGoals(currentUser.uid).catch(() => null)
      ]);

      // --- All-time stats (from full Firestore query) ---
      setAllActivities(allActivities);
      setAllTimeStats({
        total: allActivities.length,
        withReflections: allActivities.filter(a => a.reflection && a.reflection.trim()).length,
        withPhotos: allActivities.filter(a => a.imageUrl).length,
        shared: shareCount,
        elevations: elevationCount,
        causesActive: causes ? causes.length : 0
      });

      // --- This month ---
      const thisMonthActivities = yearActivities.filter(a => {
        const d = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        return d >= monthStart && d < monthEnd;
      });

      const thisMonthByCause = {};
      thisMonthActivities.forEach(a => {
        const cause = a.cause?.title || 'Unknown';
        thisMonthByCause[cause] = (thisMonthByCause[cause] || 0) + 1;
      });

      const prevMonthName = new Date(currentYear, currentMonth - 1, 1)
        .toLocaleString('en-US', { month: 'long' });

      setThisMonthData({
        total: thisMonthActivities.length,
        byCause: thisMonthByCause,
        prevMonthTotal: prevMonthActivities.length,
        prevMonthName,
        isFirstMonth: prevMonthActivities.length === 0 && thisMonthActivities.length > 0
      });

      // --- This year ---
      const thisYearActivities = yearActivities.filter(a => {
        const d = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        return d >= yearStart && d < monthEnd;
      });

      const yearByCause = {};
      const yearByMonth = {};
      thisYearActivities.forEach(a => {
        const d = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const cause = a.cause?.title || 'Unknown';
        yearByCause[cause] = (yearByCause[cause] || 0) + 1;

        const monthKey = d.toLocaleString('en-US', { month: 'long' });
        yearByMonth[monthKey] = (yearByMonth[monthKey] || 0) + 1;
      });

      let busiestMonth = '';
      let busiestMonthCount = 0;
      Object.entries(yearByMonth).forEach(([month, count]) => {
        if (count > busiestMonthCount) {
          busiestMonth = month;
          busiestMonthCount = count;
        }
      });

      setThisYearData({
        total: thisYearActivities.length,
        byCause: yearByCause,
        busiestMonth,
        busiestMonthCount
      });

      // --- Monthly chart data (last 6 months) ---
      const chartData = [];
      for (let i = 5; i >= 0; i--) {
        const m = new Date(currentYear, currentMonth - i, 1);
        const mEnd = new Date(currentYear, currentMonth - i + 1, 1);
        const monthLabel = m.toLocaleString('en-US', { month: 'short' });

        const monthActivities = yearActivities.filter(a => {
          const d = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
          return d >= m && d < mEnd;
        });

        const byCause = {};
        monthActivities.forEach(a => {
          const cause = a.cause?.title || 'Unknown';
          byCause[cause] = (byCause[cause] || 0) + 1;
        });

        chartData.push({
          month: monthLabel,
          total: monthActivities.length,
          ...byCause
        });
      }
      setMonthlyChartData(chartData);

      // --- Goal chart data (from goalHistory) ---
      if (goalData && goalData.goalHistory) {
        const goalChart = [];
        const history = [...goalData.goalHistory].sort((a, b) =>
          a.month > b.month ? 1 : -1
        );

        // Last 6 months of goal history
        const recentHistory = history.slice(-6);
        recentHistory.forEach(entry => {
          const [year, month] = entry.month.split('-');
          const monthLabel = new Date(parseInt(year), parseInt(month) - 1, 1)
            .toLocaleString('en-US', { month: 'short' });

          let goalsSet = 0;
          let goalsMet = 0;

          if (entry.goals && entry.counts) {
            Object.entries(entry.goals).forEach(([cause, target]) => {
              if (target > 0) {
                goalsSet += target;
                const count = entry.counts[cause] || 0;
                if (count >= target) goalsMet += target;
                else goalsMet += count;
              }
            });
          }

          goalChart.push({ month: monthLabel, set: goalsSet, met: goalsMet });
        });

        setGoalChartData(goalChart);
      }

    } catch (error) {
      console.error('Failed to fetch advocacy summary data:', error);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  const currentMonthName = now.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const currentYear = now.getFullYear();

  const getComparisonText = () => {
    const { total, prevMonthTotal, prevMonthName, isFirstMonth } = thisMonthData;
    if (isFirstMonth) return { text: t('summary.comparisonFirst'), color: '#808080' };
    const diff = total - prevMonthTotal;
    if (diff > 0) return { text: t('summary.comparisonMore', { diff, month: prevMonthName }), color: '#4CAF50' };
    if (diff < 0) return { text: t('summary.comparisonFewer', { diff: Math.abs(diff), month: prevMonthName }), color: '#F9A825' };
    return { text: t('summary.comparisonSame', { month: prevMonthName }), color: '#808080' };
  };

  if (loading) {
    return (
      <div className="advocacy-summary-screen">
        <div className="summary-header">
          <button className="back-arrow" onClick={() => navigate('/profile')}>←</button>
          <h2>{t('summary.title')}</h2>
        </div>
        <div className="summary-loading">{t('summary.loadingData')}</div>
        <BottomNav />
      </div>
    );
  }

  const comparison = getComparisonText();

  return (
    <div className="advocacy-summary-screen">
      <div className="summary-header">
        <button className="back-arrow" onClick={() => navigate('/profile')}>←</button>
        <h2>{t('summary.title')}</h2>
      </div>

      {allActivities.length > 0 && (
        <button
          className="export-calendar-link"
          onClick={() => downloadAllActivitiesICS(allActivities)}
          style={{
            display: 'block',
            background: 'none',
            border: 'none',
            color: '#888',
            fontSize: '13px',
            cursor: 'pointer',
            textAlign: 'right',
            width: '100%',
            padding: '0 16px 8px',
            textDecoration: 'underline'
          }}
        >
          {t('summary.exportToCalendar')}
        </button>
      )}

      {/* All-Time Stats */}
      <div className="summary-card stats-grid-card">
        <div className="stats-grid">
          <div className="stat-cell">
            <span className="stat-cell-number">{allTimeStats.total}</span>
            <span className="stat-cell-label">{t('summary.totalActivities')}</span>
          </div>
          <div className="stat-cell">
            <span className="stat-cell-number">{allTimeStats.withReflections}</span>
            <span className="stat-cell-label">{t('summary.reflections')}</span>
          </div>
          <div className="stat-cell">
            <span className="stat-cell-number">{allTimeStats.withPhotos}</span>
            <span className="stat-cell-label">{t('summary.photos')}</span>
          </div>
          <div className="stat-cell">
            <span className="stat-cell-number">{allTimeStats.shared}</span>
            <span className="stat-cell-label">{t('summary.shared')}</span>
          </div>
          <div className="stat-cell">
            <span className="stat-cell-number">{allTimeStats.elevations}</span>
            <span className="stat-cell-label">{t('summary.elevations')}</span>
          </div>
          <div className="stat-cell">
            <span className="stat-cell-number">{allTimeStats.causesActive}</span>
            <span className="stat-cell-label">{t('summary.causes')}</span>
          </div>
        </div>
      </div>

      {/* This Month */}
      <div className="summary-card">
        <h3 className="card-title">{currentMonthName}</h3>
        <div className="month-cause-list">
          {causes && causes.map(cause => (
            <div key={cause.docId} className="month-cause-row">
              <span className="month-cause-name">{cause.title}</span>
              <span className="month-cause-count">
                {thisMonthData.byCause[cause.title] || 0}
              </span>
            </div>
          ))}
        </div>
        <div className="month-total-row">
          {t('summary.totalRow', { count: thisMonthData.total })}
        </div>
        <div className="month-comparison" style={{ color: comparison.color }}>
          {comparison.text}
        </div>
      </div>

      {/* This Year */}
      <div className="summary-card">
        <h3 className="card-title">{t('summary.soFar', { year: currentYear })}</h3>
        <div className="year-total">{thisYearData.total}</div>
        <div className="year-total-label">{t('summary.totalLabel')}</div>
        <div className="month-cause-list">
          {causes && causes.map(cause => (
            <div key={cause.docId} className="month-cause-row">
              <span className="month-cause-name">{cause.title}</span>
              <span className="month-cause-count">
                {thisYearData.byCause[cause.title] || 0}
              </span>
            </div>
          ))}
        </div>
        {thisYearData.busiestMonth && (
          <div className="busiest-month">
            {t('summary.busiestMonth', { month: thisYearData.busiestMonth, count: thisYearData.busiestMonthCount })}
          </div>
        )}
      </div>

      {/* Activity Trends Chart */}
      <div className="summary-card">
        <h3 className="card-title">{t('summary.activityTrends')}</h3>
        <p className="card-subtitle">{t('summary.last6Months')}</p>
        {monthlyChartData.filter(d => d.total > 0).length >= 2 ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fontFamily: 'Inter', fill: '#808080' }}
                  axisLine={{ stroke: '#f0f0f0' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: 'Inter', fill: '#808080' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    fontFamily: 'Inter',
                    fontSize: 12,
                    borderRadius: 6,
                    border: '1px solid #eee',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, fontFamily: 'Inter' }}
                  iconType="circle"
                  iconSize={8}
                />
                <Bar
                  dataKey="Homelessness Awareness"
                  stackId="causes"
                  fill="#4A90D9"
                  radius={[0, 0, 0, 0]}
                  name="Homelessness"
                />
                <Bar
                  dataKey="Community Beautification"
                  stackId="causes"
                  fill="#4CAF50"
                  radius={[0, 0, 0, 0]}
                  name="Beautification"
                />
                <Bar
                  dataKey="Human Trafficking Awareness"
                  stackId="causes"
                  fill="#FF7043"
                  radius={[4, 4, 0, 0]}
                  name="Trafficking"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="chart-empty-state">
            {t('summary.chartEmpty')}
          </div>
        )}
      </div>

      {/* Goal Completion Chart */}
      <div className="summary-card">
        <h3 className="card-title">{t('summary.goalCompletion')}</h3>
        <p className="card-subtitle">{t('summary.last6Months')}</p>
        {goalChartData.length >= 2 ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={goalChartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fontFamily: 'Inter', fill: '#808080' }}
                  axisLine={{ stroke: '#f0f0f0' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fontFamily: 'Inter', fill: '#808080' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    fontFamily: 'Inter',
                    fontSize: 12,
                    borderRadius: 6,
                    border: '1px solid #eee',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, fontFamily: 'Inter' }}
                  iconType="circle"
                  iconSize={8}
                />
                <Bar
                  dataKey="set"
                  fill="#E0E0E0"
                  radius={[4, 4, 0, 0]}
                  name={t('summary.goalsSet')}
                />
                <Bar
                  dataKey="met"
                  fill="#4CAF50"
                  radius={[4, 4, 0, 0]}
                  name={t('summary.goalsMet')}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="chart-empty-state">
            {t('summary.goalChartEmpty')}
          </div>
        )}
      </div>

      <FeedbackLink />
      <BottomNav />
    </div>
  );
};

export default AdvocacySummaryScreen;
